/**
 * React Hook for RTDB-cached standings
 */

import { useState, useEffect, useCallback } from 'react';
import rtdbFootballService from '../rtdbFootballService';
import rtdbCache from '../rtdbCache';
import { CACHE_PATHS, getCacheKey, logCache } from '../rtdbConfig';

/**
 * Hook for fetching standings with RTDB caching
 * @param {Object} params - Query parameters
 * @param {string} params.league - League ID
 * @param {string} params.season - Season year
 * @param {boolean} params.enabled - Whether to fetch data
 * @returns {Object} Standings data, loading state, error, and refetch function
 */
export const useRTDBStandings = ({ league, season, enabled = true }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cacheStatus, setCacheStatus] = useState('idle');

  const fetchStandings = useCallback(async () => {
    if (!enabled || !league || !season) return;

    try {
      setLoading(true);
      setError(null);
      setCacheStatus('fetching');
      
      const response = await rtdbFootballService.getStandings(Number(league), season);
      setData(response.response?.[0]?.league?.standings?.[0] || []);
      setCacheStatus('success');
    } catch (err) {
      setError(err.message || 'Failed to load standings');
      setCacheStatus('error');
      console.error('Failed to load standings:', err);
    } finally {
      setLoading(false);
    }
  }, [league, season, enabled]);

  // Set up subscription (standings don't need real-time updates as frequently)
  useEffect(() => {
    if (!enabled || !league || !season) return;

    const cachePath = `${CACHE_PATHS.STANDINGS}/${getCacheKey('standings', league, season)}`;
    
    // Subscribe to updates
    const unsubscribe = rtdbCache.subscribe(cachePath, (cachedData) => {
      if (cachedData) {
        logCache('info', 'Standings update received', { league, season });
        setData(cachedData.response?.[0]?.league?.standings?.[0] || []);
        setCacheStatus('cache-update');
      } else {
        fetchStandings();
      }
    });

    // Initial fetch
    fetchStandings();

    // Refresh every 5 minutes (standings don't change often)
    const interval = setInterval(fetchStandings, 5 * 60 * 1000);

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, [league, season, enabled]); // Don't include fetchStandings

  return { 
    data, 
    loading, 
    error, 
    refetch: fetchStandings,
    cacheStatus,
    isConnected: rtdbCache.isConnected()
  };
};